import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { ClipboardCheck, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";

const CHECKLIST_DEFAULTS = {
  pre_list_seller: [
    { label: "Confirm property details (beds, baths, square footage, upgrades)", completed: false },
    { label: "Complete required seller disclosures", completed: false },
    { label: "Identify any known issues that need to be disclosed", completed: false },
    { label: "Gather HOA information (if applicable)", completed: false },
    { label: "Address obvious repair issues that could affect buyer interest", completed: false },
    { label: "Ensure all major systems are functioning (HVAC, plumbing, electrical)", completed: false },
    { label: "Remove clutter and personal items from main living areas", completed: false },
    { label: "Ensure the home is clean and presentable for photos and showings", completed: false },
    { label: "Confirm readiness for listing photos", completed: false },
    { label: "Confirm how showings will be handled (lockbox, scheduling, etc.)", completed: false },
    { label: "Secure or plan for pets during showings", completed: false },
    { label: "Identify any items that will NOT be included in the sale", completed: false },
  ],
  pre_close_seller: [
    { label: "Leave all keys, garage openers, and access devices", completed: false },
    { label: "Remove all personal belongings from the property", completed: false },
    { label: "Clean the home (basic or professional)", completed: false },
    { label: "Remove trash and debris from the property", completed: false },
    { label: "Confirm utilities are scheduled for transfer or cancellation", completed: false },
  ],
  pre_close_buyer: [
    { label: "Schedule utilities to be transferred into your name", completed: false },
    { label: "Obtain homeowner's insurance", completed: false },
    { label: "Review closing disclosure documents", completed: false },
    { label: "Schedule final walkthrough", completed: false },
    { label: "Confirm funds for closing", completed: false },
    { label: "Bring required ID and documents to closing", completed: false },
  ],
};

const CHECKLIST_TITLES = {
  pre_list_seller: "Seller Pre-List Checklist",
  pre_close_seller: "Seller Pre-Close Checklist",
  pre_close_buyer: "Buyer Pre-Close Checklist",
};

export const TransactionChecklist = ({ transactionId, checklistKey, checklists, onUpdate }) => {
  const defaults = CHECKLIST_DEFAULTS[checklistKey] || [];
  const title = CHECKLIST_TITLES[checklistKey] || "Checklist";

  // Migrate from old pre_close_checklist field for seller pre-close
  const [items, setItems] = useState(() => {
    const stored = checklists?.[checklistKey];
    if (Array.isArray(stored) && stored.length > 0) return stored;
    return defaults;
  });
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized) {
      const stored = checklists?.[checklistKey];
      if (!Array.isArray(stored) || stored.length === 0) {
        // Persist defaults
        const updated = { ...(checklists || {}), [checklistKey]: defaults };
        supabase
          .from("transactions")
          .update({ checklists: updated })
          .eq("id", transactionId)
          .then(() => {
            setInitialized(true);
            onUpdate?.({ checklists: updated });
          });
      } else {
        setInitialized(true);
      }
    }
  }, []);

  const completedCount = items.filter((i) => i.completed).length;

  const toggleItem = async (index) => {
    const updated = items.map((item, i) => i === index ? { ...item, completed: !item.completed } : item);
    setItems(updated);
    try {
      const newChecklists = { ...(checklists || {}), [checklistKey]: updated };
      const { error } = await supabase
        .from("transactions")
        .update({ checklists: newChecklists })
        .eq("id", transactionId);
      if (error) throw error;
      onUpdate?.({ checklists: newChecklists });
    } catch (err) {
      console.error("[Checklist] Save error:", err);
      setItems(items);
      toast.error("Failed to update checklist");
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-100 shadow-soft p-6 my-6" data-testid={`checklist-${checklistKey}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
          <ClipboardCheck className="w-[18px] h-[18px] text-slate-400" strokeWidth={1.75} />
          {title}
        </h3>
        <span className="text-[13px] text-slate-500" data-testid={`checklist-progress-${checklistKey}`}>
          {completedCount} of {items.length} completed
        </span>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 rounded-full bg-slate-100 mb-5">
        <div
          className="h-full rounded-full bg-emerald-500 transition-all duration-300"
          style={{ width: `${items.length > 0 ? (completedCount / items.length) * 100 : 0}%` }}
        />
      </div>

      {/* Checklist items */}
      <div className="space-y-0.5">
        {items.map((item, index) => (
          <button
            key={index}
            onClick={() => toggleItem(index)}
            className="w-full flex items-start gap-3 py-3 px-1 rounded-md hover:bg-slate-50 transition-colors text-left group"
            data-testid={`checklist-item-${checklistKey}-${index}`}
          >
            {item.completed ? (
              <CheckSquare className="w-[18px] h-[18px] text-emerald-500 shrink-0 mt-0.5" />
            ) : (
              <Square className="w-[18px] h-[18px] text-slate-300 group-hover:text-slate-400 shrink-0 mt-0.5" />
            )}
            <span className={`text-sm leading-snug ${
              item.completed ? "text-slate-400 line-through" : "text-slate-700"
            }`}>
              {item.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};
